import { aiCall } from './aiProxy';
import { getUserProfile } from '../utils/storage';
import { CREDIT_COSTS } from '../utils/credits';

const DEV = import.meta.env.DEV;

/* ───────────────────── Intent-based tone mapping ───────────────────── */

const INTENT_INSTRUCTIONS = {
  curious: `The user is "Just Curious" — they're in early research mode. Use an educational tone. Give a broad overview of the model: what makes it desirable, general price ranges, what to know before diving deeper. Don't overwhelm with specifics.`,
  passive: `The user is "Passively Looking" — they're monitoring the market. Focus on value trends, what constitutes a good deal, seasonal pricing patterns, and how to recognize below-market opportunities. Help them understand when to pounce.`,
  active: `The user is "Actively Shopping" — they're comparing specific examples. Be detailed and actionable: specific comparable sales, red flags to watch for, negotiation tactics, and concrete price recommendations. Every insight should help them make a decision.`,
  ready: `The user is "Ready to Buy" — they're about to make an offer. Focus on risk mitigation: PPI checklist items, financing options, insurance considerations, and immediate next steps. Be direct and action-oriented. Include specific offer/walk-away price guidance.`,
};

/* ───────────────────── System prompts ───────────────────── */

const BASE_SYSTEM_PROMPT = `You are Auto Advisor, an expert automotive market analyst and pre-purchase consultant. You help buyers make informed vehicle purchase decisions using real market data.

For market valuations, you MUST use web_search to find recent actual sales data from these sources: Bring a Trailer, Cars & Bids, Hemmings, eBay Motors sold listings, and other collector car marketplaces. NEVER use KBB, NADA, or Edmunds for collector, enthusiast, exotic, or modified vehicles. Search for completed auctions and recent sales from the past 6-12 months.

Search strategy:
1. PRIMARY: Bring a Trailer, Cars & Bids, eBay Motors sold listings
2. SECONDARY: Hemmings, Autotrader, Classic.com
3. Run 3-4 searches with varied queries targeting different sources

If web search returns no useful results, set recentComps to an empty array and note "Unable to find recent comparable sales data". Do NOT fabricate sales data.

Adapt recommendations based on the buyer's mechanical experience level and budget context when provided. For beginners, emphasize PPI importance and professional help. For experts, include technical details and DIY opportunities. When a ZIP code is provided, use it for regional pricing context.`;

const OUTPUT_INSTRUCTIONS = `
Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "vehicleLabel": string,
  "marketValuation": {
    "fairRange": string (e.g. "$75,000 - $82,000"),
    "askingPriceAssessment": string or null (e.g. "12% above market" or null if no asking price),
    "assessment": string (e.g. "Fair Deal", "Proceed with Caution", "Strong Buy"),
    "recentComps": [
      {
        "source": string,
        "price": string,
        "date": string,
        "details": string
      }
    ],
    "priceRecommendation": string,
    "trend": "appreciating" | "stable" | "declining",
    "trendNote": string
  },
  "vehiclePerspectives": {
    "consumer": string,
    "enthusiast": string,
    "collector": string,
    "mechanic": string
  },
  "photoAnalysis": [
    {
      "category": string,
      "finding": string,
      "severity": "good" | "minor" | "caution" | "major"
    }
  ],
  "ppiChecklist": [
    {
      "text": string,
      "priority": "high" | "medium" | "low"
    }
  ],
  "costToOwn": {
    "items": [
      { "label": string, "rating": "Below Average" | "Average" | "Above Average", "note": string }
    ],
    "summary": string
  },
  "usabilityScores": {
    "dailyDriver": number (1-10),
    "dailyDriverNote": string,
    "weekendCar": number (1-10),
    "weekendCarNote": string
  },
  "expertInsights": [string],
  "isDealerListing": boolean,
  "negotiationTips": [string]
}

Notes on specific fields:
- vehiclePerspectives: Each perspective is a short paragraph (max 10 sentences) giving an opinionated take on this vehicle. Consumer = practicality and value for money. Enthusiast = driving experience and fun factor. Collector = investment potential, rarity, and desirability. Mechanic = reliability, maintenance burden, and common repair needs.
- usabilityScores: Rate this vehicle as a daily driver (commuting, errands, practicality) and as a weekend/fun car (driving engagement, presence, enjoyment). Provide a brief rationale for each score.
- costToOwn: Rate each cost category relative to the average vehicle in this segment. Use "Below Average" (cheaper than typical), "Average", or "Above Average" (more expensive than typical). Add a brief note explaining why.
- isDealerListing: Set to true if the listing appears to be from a dealership, false if it appears to be a private party sale. If uncertain, default to true.`;

/* ───────────────────── Tool definition (sent to server) ───────────────────── */

const WEB_SEARCH_TOOL = {
  type: 'function',
  function: {
    name: 'web_search',
    description:
      'Search the web for vehicle sales data, auction results, market valuations, common issues, and ownership costs from automotive marketplaces and forums.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'The search query. Include vehicle details and target sources.',
        },
      },
      required: ['query'],
    },
  },
};

const MAX_TOOL_ROUNDS = 8;

/* ───────────────────── Analyze a listing URL ───────────────────── */

function buildConditionPrompt(condition, isPhotos) {
  if (!condition) return '';

  let guidance = `\nThe user reports the vehicle condition as: ${condition}.`;

  guidance += `\nAdjust comparable sales to account for condition differences. A Concours example commands 20-30% premium over Good condition. A Fair condition example should be 15-25% below market average. Factor this into your value range and price recommendation.`;

  if (isPhotos) {
    guidance += `\nSeller claims ${condition} condition. Compare your photo analysis findings against this claim. Flag any discrepancies (e.g., claims Excellent but photos show curb rash, panel gaps, worn interior).`;
  }

  if (condition === 'Non-Running Project' || condition === 'Parts Car') {
    guidance += `\nThis is a project vehicle. Focus your analysis on project viability: parts availability, restoration cost estimates, donor car value, and realistic total investment to reach roadworthy condition. De-emphasize typical ownership costs like insurance and fuel.`;
  }

  return guidance;
}

function buildProfileContext() {
  const profile = getUserProfile();
  if (!profile) return '';
  const parts = [];
  if (profile.zipCode) parts.push(`Buyer Location (ZIP): ${profile.zipCode}`);
  if (profile.mechanicalLevel) parts.push(`Mechanical Level: ${profile.mechanicalLevel}`);
  if (profile.salaryRange && profile.salaryRange !== 'Prefer not to say') parts.push(`Budget Context: ${profile.salaryRange}`);
  if (profile.primaryUseCases?.length) parts.push(`Buyer Use Cases: ${profile.primaryUseCases.join(', ')}`);
  return parts.length ? `\n\nBuyer Profile:\n${parts.join('\n')}` : '';
}

export async function analyzeListingURL(data, signal, onStage) {
  const intentInstruction = INTENT_INSTRUCTIONS[data.intent] || INTENT_INSTRUCTIONS.active;
  const conditionPrompt = buildConditionPrompt(data.condition, false);

  const systemPrompt = `${BASE_SYSTEM_PROMPT}

${intentInstruction}

The user has provided a listing URL. First, use web_search to access and understand the listing. Then search for comparable recent sales to assess the asking price. Research common issues for this model.
${conditionPrompt}

${OUTPUT_INSTRUCTIONS}

Set "photoAnalysis" to an empty array since we're analyzing a URL, not photos.`;

  const userPrompt = `Analyze this vehicle listing and provide a comprehensive pre-purchase report.

Listing URL: ${data.listingUrl}
${data.askingPrice ? `Asking Price: ${data.askingPrice}` : ''}
${data.condition ? `Reported Condition: ${data.condition}` : ''}
${data.budgetMin || data.budgetMax ? `Budget Range: ${data.budgetMin || '?'} - ${data.budgetMax || '?'}` : ''}${buildProfileContext()}

Search the listing URL, then find comparable recent sales and research common issues. Provide analysis as JSON.`;

  const { result } = await aiCall({
    action: 'chat_tools',
    creditCost: CREDIT_COSTS.autoAdvisor,
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ],
    tools: [WEB_SEARCH_TOOL],
    temperature: 0.4,
    max_tokens: 2000,
    maxToolRounds: MAX_TOOL_ROUNDS,
  }, { signal, timeout: 120000 });

  return result;
}

/* ───────────────────── Analyze uploaded photos ───────────────────── */

export async function analyzePhotos(data, signal, onStage) {
  const intentInstruction = INTENT_INSTRUCTIONS[data.intent] || INTENT_INSTRUCTIONS.active;
  const conditionPrompt = buildConditionPrompt(data.condition, true);

  const systemPrompt = `${BASE_SYSTEM_PROMPT}

${intentInstruction}

The user has uploaded photos of a vehicle listing. Analyze the photos for condition, damage, modifications, and authenticity concerns. Then research the model for market data and common issues.
${conditionPrompt}

${OUTPUT_INSTRUCTIONS}

For "photoAnalysis", provide detailed findings from each photo. Categorize as: Exterior, Interior, Engine Bay, Wheels/Tires, Undercarriage, Dashboard, Seats, Paint, or Other.`;

  // Build content array with images for GPT-4 Vision
  const content = [
    {
      type: 'text',
      text: `Analyze these ${data.listingPhotos.length} photos of a vehicle listing. Identify the vehicle if possible, then provide a full pre-purchase analysis.

${data.askingPrice ? `Asking Price: ${data.askingPrice}` : ''}
${data.condition ? `Reported Condition: ${data.condition}` : ''}
${data.budgetMin || data.budgetMax ? `Budget Range: ${data.budgetMin || '?'} - ${data.budgetMax || '?'}` : ''}${buildProfileContext()}

After analyzing the photos:
1. Identify the vehicle (year, make, model if possible)
2. Search for comparable recent sales
3. Research common issues for this model
${data.condition ? `4. Compare photo findings against the reported "${data.condition}" condition — flag any discrepancies` : ''}
5. Provide your complete analysis as JSON`,
    },
    ...data.listingPhotos.map((photo) => ({
      type: 'image_url',
      image_url: { url: photo.dataUrl, detail: 'high' },
    })),
  ];

  const { result } = await aiCall({
    action: 'chat_tools',
    creditCost: CREDIT_COSTS.autoAdvisorPhoto,
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content },
    ],
    tools: [WEB_SEARCH_TOOL],
    temperature: 0.4,
    max_tokens: 2000,
    maxToolRounds: MAX_TOOL_ROUNDS,
  }, { signal, timeout: 120000 });

  return result;
}

/* ───────────────────── Analyze general vehicle research ───────────────────── */

export async function analyzeGeneralVehicle(data, signal, onStage) {
  const intentInstruction = INTENT_INSTRUCTIONS[data.intent] || INTENT_INSTRUCTIONS.curious;
  const conditionPrompt = buildConditionPrompt(data.condition, false);

  const vehicleDesc = `${data.year} ${data.make} ${data.model}${data.trim ? ` ${data.trim}` : ''}`;

  const systemPrompt = `${BASE_SYSTEM_PROMPT}

${intentInstruction}

The user wants general research on a specific model. Provide a comprehensive buyer's guide with real market data. Focus on what they need to know before purchasing.
${conditionPrompt}

${OUTPUT_INSTRUCTIONS}

Set "photoAnalysis" to an empty array since no photos were provided.`;

  const userPrompt = `Provide a comprehensive pre-purchase buyer's guide for:

Vehicle: ${vehicleDesc}
${data.mileage ? `Mileage: ${data.mileage} miles` : ''}
${data.condition ? `Target Condition: ${data.condition}` : ''}
${data.intendedUse ? `Intended Use: ${Array.isArray(data.intendedUse) ? data.intendedUse.join(', ') : data.intendedUse}` : ''}
${data.budgetMin || data.budgetMax ? `Budget Range: ${data.budgetMin || '?'} - ${data.budgetMax || '?'}` : ''}${buildProfileContext()}

Search for comparable recent sales, research common issues, and look up ownership costs. Provide analysis as JSON with "vehicleLabel": "${vehicleDesc}".`;

  const { result } = await aiCall({
    action: 'chat_tools',
    creditCost: CREDIT_COSTS.autoAdvisor,
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ],
    tools: [WEB_SEARCH_TOOL],
    temperature: 0.4,
    max_tokens: 2000,
    maxToolRounds: MAX_TOOL_ROUNDS,
  }, { signal, timeout: 120000 });

  return result;
}

/* ───────────────────── Analyze repair quote ───────────────────── */

const REPAIR_SYSTEM_PROMPT = `${BASE_SYSTEM_PROMPT}

You are analyzing a repair quote to help the vehicle owner understand if the pricing is fair. Use web_search to research typical repair costs for the specific vehicle, repair type, and geographic region (based on ZIP code).

Search strategy for repair quotes:
1. Search for the specific repair on the specific vehicle (e.g. "2018 Honda Civic timing belt replacement cost")
2. Search for regional labor rates if ZIP code is provided
3. Search for parts pricing (OEM vs aftermarket)
4. Compare with common repair cost databases

Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "vehicleLabel": string,
  "verdict": "low" | "fair" | "high",
  "verdictLabel": string (e.g. "Great Price", "Fair Price", "Above Average"),
  "verdictNote": string,
  "quotedTotal": string,
  "fairRange": string (e.g. "$800 - $1,200"),
  "sections": [
    {
      "id": "breakdown",
      "title": "Line Item Breakdown",
      "emoji": "📋",
      "items": [
        {
          "label": string,
          "fairRange": string,
          "quotedPrice": string,
          "assessment": "low" | "fair" | "high"
        }
      ]
    },
    {
      "id": "questions",
      "title": "Questions to Ask",
      "emoji": "❓",
      "tips": [string]
    },
    {
      "id": "diy",
      "title": "DIY Assessment",
      "emoji": "🔧",
      "difficulty": string,
      "partsEstimate": string,
      "timeEstimate": string,
      "note": string
    },
    {
      "id": "alternatives",
      "title": "Alternative Options",
      "emoji": "💡",
      "tips": [string]
    }
  ]
}`;

export async function analyzeRepairQuote(data, signal, onStage) {
  const creditCost = data.repairQuotePhoto ? CREDIT_COSTS.repairQuotePhoto : CREDIT_COSTS.repairQuote;
  const vehicleDesc = `${data.repairVehicleYear || ''} ${data.repairVehicleMake || ''} ${data.repairVehicleModel || ''}`.trim();

  if (data.repairQuotePhoto) {
    // Photo mode - use GPT-4 Vision to extract and analyze
    const content = [
      {
        type: 'text',
        text: `Analyze this repair quote/estimate photo and provide a comprehensive fairness analysis.

Vehicle: ${vehicleDesc || 'See quote for details'}
${data.repairMileage ? `Mileage: ${data.repairMileage}` : ''}
${data.repairZipCode ? `ZIP Code: ${data.repairZipCode}` : ''}${buildProfileContext()}

Steps:
1. Extract all repair details, line items, and pricing from the photo
2. Search for fair pricing for each repair item on this specific vehicle
3. Compare quoted prices against market rates
4. Provide your analysis as JSON`,
      },
      {
        type: 'image_url',
        image_url: { url: data.repairQuotePhoto, detail: 'high' },
      },
    ];

    const { result } = await aiCall({
      action: 'chat_tools',
      creditCost,
      model: 'gpt-4o',
      messages: [
        { role: 'system', content: REPAIR_SYSTEM_PROMPT },
        { role: 'user', content },
      ],
      tools: [WEB_SEARCH_TOOL],
      temperature: 0.4,
      max_tokens: 2000,
      maxToolRounds: MAX_TOOL_ROUNDS,
    }, { signal, timeout: 120000 });

    return result;
  }

  // Manual mode
  const lineItemsText = (data.repairLineItems || [])
    .filter((li) => li.description)
    .map((li) => `  - ${li.description}: ${li.cost || 'no price listed'}`)
    .join('\n');

  const userPrompt = `Analyze this repair quote for fairness:

Vehicle: ${vehicleDesc}
${data.repairMileage ? `Mileage: ${data.repairMileage}` : ''}
${data.repairZipCode ? `Location (ZIP): ${data.repairZipCode}` : ''}
${data.repairShopName ? `Shop: ${data.repairShopName}` : ''}

Repair Description: ${data.repairDescription || 'Not specified'}
Total Quoted: ${data.repairTotalQuoted || 'Not specified'}
${lineItemsText ? `\nLine Items:\n${lineItemsText}` : ''}${buildProfileContext()}

Search for typical costs for this repair on this vehicle and regional labor rates. Provide analysis as JSON.`;

  const { result } = await aiCall({
    action: 'chat_tools',
    creditCost,
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: REPAIR_SYSTEM_PROMPT },
      { role: 'user', content: userPrompt },
    ],
    tools: [WEB_SEARCH_TOOL],
    temperature: 0.4,
    max_tokens: 2000,
    maxToolRounds: MAX_TOOL_ROUNDS,
  }, { signal, timeout: 120000 });

  return result;
}

/* ───────────────────── Parse repair quote response ───────────────────── */

export function parseRepairQuoteResponse(raw, data) {
  const cleaned = raw.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  const parsed = JSON.parse(cleaned);

  return {
    vehicleLabel: parsed.vehicleLabel || `${data.repairVehicleYear || ''} ${data.repairVehicleMake || ''} ${data.repairVehicleModel || ''}`.trim() || 'Vehicle Repair',
    verdict: parsed.verdict || 'fair',
    verdictLabel: parsed.verdictLabel || 'Fair Price',
    verdictNote: parsed.verdictNote || '',
    quotedTotal: parsed.quotedTotal || data.repairTotalQuoted || 'Not specified',
    fairRange: parsed.fairRange || 'Unable to determine',
    sections: parsed.sections || [],
  };
}

/* ───────────────────── Parse response into display format ───────────────────── */

export function parseAnalysisResponse(raw, data) {
  const cleaned = raw.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  const parsed = JSON.parse(cleaned);

  const mv = parsed.marketValuation || {};
  const vp = parsed.vehiclePerspectives || parsed.vehicleIntelligence || {};
  const us = parsed.usabilityScores || {};
  const pa = parsed.photoAnalysis || [];
  const ppi = parsed.ppiChecklist || [];
  const cto = parsed.costToOwn || {};
  const ei = parsed.expertInsights || [];
  const nt = parsed.negotiationTips || [];
  const isDealerListing = parsed.isDealerListing !== false; // default true if not specified

  const sections = [];

  // 1. Market Valuation
  sections.push({
    id: 'valuation',
    title: 'Market Valuation',
    emoji: '\uD83D\uDCB0',
    content: {
      range: mv.fairRange || 'Unable to determine',
      trend: mv.trend || 'stable',
      trendNote: mv.trendNote || '',
      comparables: (mv.recentComps || []).map(
        (c) => `${c.source}: ${c.price} \u2013 ${c.details} (${c.date})`,
      ),
      verdict: mv.askingPriceAssessment
        ? `${mv.assessment || 'Assessment'}: ${mv.askingPriceAssessment}. ${mv.priceRecommendation || ''}`
        : mv.priceRecommendation || null,
    },
  });

  // 2. Vehicle Perspectives (replaces Vehicle Intelligence)
  const hasPerspectives = vp.consumer || vp.enthusiast || vp.collector || vp.mechanic;
  if (hasPerspectives) {
    sections.push({
      id: 'perspectives',
      title: 'Vehicle Perspectives',
      emoji: '\uD83E\uDDE0',
      content: {
        perspectives: [
          { key: 'consumer', emoji: '\uD83D\uDC64', label: 'Consumer Take', text: vp.consumer || '' },
          { key: 'enthusiast', emoji: '\uD83C\uDFCE\uFE0F', label: 'Enthusiast Take', text: vp.enthusiast || '' },
          { key: 'collector', emoji: '\uD83C\uDFC6', label: 'Collector Take', text: vp.collector || '' },
          { key: 'mechanic', emoji: '\uD83D\uDD27', label: 'Mechanic Take', text: vp.mechanic || '' },
        ].filter((p) => p.text),
      },
    });
  } else if (vp.summary || vp.pros || vp.cons) {
    // Fallback for older AI responses that still use the old format
    sections.push({
      id: 'perspectives',
      title: 'Vehicle Intelligence',
      emoji: '\uD83E\uDDE0',
      content: {
        perspectives: [
          { key: 'summary', emoji: '\uD83E\uDDE0', label: 'Overview', text: [vp.summary, ...(vp.pros || []).map((p) => `+ ${p}`), ...(vp.cons || []).map((c) => `- ${c}`)].filter(Boolean).join('\n') },
        ],
      },
    });
  }

  // 2b. Usability Scores (Daily vs Weekend)
  if (us.dailyDriver || us.weekendCar) {
    sections.push({
      id: 'usability',
      title: 'Daily vs Weekend Use',
      emoji: '\uD83D\uDE97',
      content: {
        dailyScore: us.dailyDriver || 5,
        dailyNote: us.dailyDriverNote || '',
        weekendScore: us.weekendCar || 5,
        weekendNote: us.weekendCarNote || '',
      },
    });
  }

  // 3. Photo Analysis (only if photos were provided and results exist)
  if (pa.length > 0) {
    sections.push({
      id: 'photos',
      title: 'Photo Analysis',
      emoji: '\uD83D\uDCF8',
      content: {
        summary: `${pa.length} finding(s) from photo analysis.`,
        observations: pa.map(
          (p) => `[${p.severity?.toUpperCase() || 'INFO'}] ${p.category}: ${p.finding}`,
        ),
      },
    });
  }

  // 4. Pre-Purchase Checklist
  if (ppi.length > 0) {
    sections.push({
      id: 'checklist',
      title: 'Pre-Purchase Checklist',
      emoji: '\u2705',
      content: {
        items: ppi.map((item) =>
          typeof item === 'string'
            ? { text: item, priority: 'medium' }
            : { text: item.text, priority: item.priority || 'medium' },
        ),
      },
    });
  }

  // 5. Cost-to-Own
  if (cto.items && cto.items.length > 0) {
    sections.push({
      id: 'cost',
      title: 'Cost-to-Own (First Year)',
      emoji: '\uD83D\uDCCA',
      content: {
        items: cto.items.map((item) => ({
          label: item.label,
          rating: item.rating || 'Average',
          note: item.note || item.value || '',
        })),
        summary: cto.summary || cto.total || '',
      },
    });
  }

  // 6. Expert Insights
  if (ei.length > 0) {
    sections.push({
      id: 'insights',
      title: 'Expert Insights',
      emoji: '\uD83D\uDD27',
      content: { tips: ei },
    });
  }

  // 7. Negotiation Tips
  if (nt.length > 0) {
    sections.push({
      id: 'negotiation',
      title: 'Negotiation Tips',
      emoji: '\uD83E\uDD1D',
      content: { tips: nt, isDealerListing },
    });
  }

  return {
    vehicleLabel: parsed.vehicleLabel || data.vehicleLabel || 'Vehicle Analysis',
    sections,
  };
}
